"use client";

import { useMemo } from "react";
import { RegisterData } from "@/modules/types/auth";
import { apiClient } from "@/lib/api/api-client";
import { useAuthStore } from "@/modules/store/authStore";
import { authService } from "@/modules/services/authService";
import { usePathname, useRouter } from "next/navigation";
import { routes } from "@/lib/utils/routes";
import { trackEvent, setAnalyticsUser } from "@/lib/analytics";

export function useAuth() {
  const {
    token,
    setToken,
    setRefreshToken,
    logout: logoutStore,
  } = useAuthStore();
  const pathname = usePathname();
  const router = useRouter();

  const isAuthenticated = useMemo(() => {
    return !!token;
  }, [token]);

  const login = async (
    email: string,
    password: string,
    isSaveLogin?: boolean,
    provider?: string,
    providerId?: string,
    fullName?: string
  ) => {
    const response = await authService.login({
      email,
      password,
      isSaveLogin,
      provider,
      providerId,
      fullName,
    });
    apiClient.setToken(response.token);
    setToken({
      token: response.token,
      expired: response.expiredAt,
    });
    setRefreshToken({
      refreshToken: response.refreshToken,
      expired: response.expiredAt + 3600,
    });

    const method = provider || "email";
    trackEvent("login", { method });
    const authId =
      (response as any)?.auth_id ||
      (response as any)?.authId ||
      (response as any)?.id;
    if (authId) {
      setAnalyticsUser(authId);
    }

    return response;
  };

  const register = async (userData: RegisterData) => {
    const response = await authService.register(userData);
    apiClient.setToken(response.token);
    setToken({
      token: response.token,
      expired: response.expiredAt,
    });
    setRefreshToken({
      refreshToken: response.refreshToken,
      expired: response.expiredAt + 3600,
    });

    trackEvent("sign_up", { method: "email" });
    const authId =
      (response as any)?.auth_id ||
      (response as any)?.authId ||
      (response as any)?.id;
    if (authId) {
      setAnalyticsUser(authId);
    }

    return response;
  };

  const onLogout = () => {
    logoutStore();
    setAnalyticsUser(null);
    if (pathname.includes("candidate")) {
      router.push(routes.home);
    } else {
      router.refresh();
    }
  };

  return {
    login,
    register,
    logout: onLogout,
    isAuthenticated,
  };
}
